import React, { Fragment, useEffect, useRef } from 'react'
import { Navigate, useLocation } from 'react-router'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import NProgress from 'nprogress'
import { Helmet } from 'react-helmet'
import { history } from 'index'
import store from 'store'
import { deleteUMKFromLocal, getUMKFromLocal } from 'pUtils'
import { getEnvParam, getTempEnvParam, setTempEnvParam } from 'env'
import getRoutes from 'services/router'
import { SERVICE_TITLE, SERVICE_NAME_TITLE } from 'service'
import { DEFAULT_AUTH_PAGE } from 'utils'
import { errorBase } from 'utils/ui'
import { prefetchCrepasViewerOnIdle } from 'utils/crepasLoader'
import PasskeyAutoFlow from 'pages/auth/components/PasskeyAutoFlow'
import PublicLayout from './Public'
import AuthLayout from './Auth'
import MainLayout from './Main'
import AdminLayout from './Admin'
import EmptyLayout from './Empty'

const Layouts = {
  public: PublicLayout,
  auth: AuthLayout,
  main: MainLayout,
  admin: AdminLayout,
  provisioning: PublicLayout,
  empty: EmptyLayout,
}

let previousPathname = ''

const publicList = ['/publicPage']

const mapStateToProps = ({ user }) => ({
  loading: user.loading,
  // provisioning: user.provisioning,
  authorized: user.authorized,
  authType: user.authType,
  licenseTerminated: user.licenseTerminated,
})

const Layout = ({ loading, authorized, children, authType, dispatch, licenseTerminated }) => {
  const location = useLocation()
  const { pathname } = location
  const { t } = useTranslation()
  // PR-F: 종료 안내 팝업 1회 가드(동시 다발 응답으로 licenseTerminated가 여러 번 세팅돼도 1회).
  const licenseModalShownRef = useRef(false)

  useEffect(() => {
    // dragdocs(crepas-viewer) 리소스를 앱 첫 렌더링 이후 idle 타임에 prefetch.
    // index.html 의 window.load 시점은 SPA 첫 화면 렌더링보다 일러 라우트
    // 청크·API 호출과 대역폭을 경쟁했음 (위키: dragdocs-integration 룰 2)
    prefetchCrepasViewerOnIdle()
  }, [])

  useEffect(() => {
    // PR-F: KR 라이선스/계약 종료(890/891) 사용중 감지 → 안내 팝업 후 로그아웃.
    // OX-4286: 822(로그인 차단)도 동일 안내 팝업(문구=licenseInactive "서비스 접근 권한이 없습니다")으로 편입.
    // restClient가 SET_STATE{licenseTerminated} 세팅 → 여기서 관찰. onOk 시 기존
    // user/LOGOUT{broadcast:true}(officenext /api/logout + 로컬 정리 + 로그인 이동 + 다중탭) 재사용.
    if (licenseTerminated === 890 || licenseTerminated === 891 || licenseTerminated === 822) {
      if (!licenseModalShownRef.current) {
        licenseModalShownRef.current = true
        // 890(계약 만료)만 contractExpired, 891(라이선스 비활성)·822(로그인 차단)은 licenseInactive.
        const msgKey =
          licenseTerminated === 890 ? 'license.contractExpired' : 'license.licenseInactive'
        errorBase({
          title: t(msgKey, { service: t(SERVICE_NAME_TITLE) }),
          // 종료는 되돌릴 수 없음 → 닫기/mask 비활성화, 확인(OK)으로만 로그아웃 진행
          closable: false,
          maskClosable: false,
          onOk: () => {
            dispatch({ type: 'user/LOGOUT', payload: { broadcast: true } })
          },
        })
      }
    } else {
      // null 복귀(LOGOUT 리셋) 시 가드 해제 → 재로그인 후 재발 대응
      licenseModalShownRef.current = false
    }
  }, [licenseTerminated, dispatch, t])

  useEffect(() => {
    const channels = []
    try {
      const ipBc = new BroadcastChannel('ip-blocked')

      ipBc.onmessage = (event) => {
        if (event.data?.type === 'ip-blocked') {
          setTempEnvParam('ipBlocked', { blocked: true, timestamp: Date.now() })
          history.push('/ip-blocked')

          if (store.get('app.user.token')) {
            setTimeout(() => {
              dispatch({ type: 'user/LOGOUT' })
            }, 100)
          }
        }
      }
      channels.push(ipBc)

      // 다중 탭 동기 로그아웃 수신: 다른 탭이 사용자 로그아웃을 broadcast 하면 이
      // 탭도 로그아웃한다. fromBroadcast 플래그로 ① 재-broadcast(무한 루프) 와
      // ② 서버 로그아웃 API 재호출을 막는다. 토큰 유무로 게이팅하지 않는다 —
      // 로그아웃 saga 가 authorized/token 을 비우면 layout 가드가 보호 라우트를
      // 자동으로 auth/login 으로 redirect 한다 (public/viewer 페이지는 유지).
      const logoutBc = new BroadcastChannel('user-logout')
      logoutBc.onmessage = (event) => {
        if (event.data?.type === 'user-logout') {
          dispatch({ type: 'user/LOGOUT', payload: { fromBroadcast: true } })
        }
      }
      channels.push(logoutBc)

      const handleExtensionLogout = (event) => {
        // 익스텐션 content script(isolated world)에서 보낸 postMessage. event.source
        // 비교는 isolated world 와 page main world 간 window 동일성 보장이 안 돼
        // 사용하지 않고, origin + 고정 type 시그니처로만 검증한다.
        if (event.origin !== window.location.origin) return
        if (event.data?.type !== 'safebox/EXT_LOGOUT') return
        if (!store.get('app.user.token')) return
        dispatch({ type: 'user/LOGOUT', payload: { fromExtension: true } })
      }
      window.addEventListener('message', handleExtensionLogout)

      return () => {
        channels.forEach((bc) => bc.close())
        window.removeEventListener('message', handleExtensionLogout)
      }
    } catch (e) {
      return undefined
    }
  }, [dispatch])

  if (previousPathname !== pathname) {
    window.scrollTo(0, 0)
    NProgress.start()
  }
  setTimeout(() => {
    NProgress.done()
    previousPathname = pathname
  }, 300)

  // Layout Rendering
  let needAuth = true
  const getLayout = () => {
    if (pathname === '/') {
      return 'public'
    }
    if (
      publicList.findIndex((publicItem) => {
        return pathname.indexOf(publicItem) === 0
      }) > -1
    ) {
      needAuth = false
      return 'public'
    }

    if (/^\/auth(?=\/|$)/i.test(pathname)) {
      needAuth = false
      return 'auth'
    }
    if (/^\/masterPW(?=\/|$)/i.test(pathname)) {
      needAuth = true
      return 'auth'
    }
    if (/^\/admin(?=\/|$)/i.test(pathname)) {
      needAuth = true
      return 'admin'
    }

    if (/^\/invite(?=\/|$)/i.test(pathname)) {
      needAuth = false
      return 'empty'
    }

    if (/^\/sso-test(?=\/|$)/i.test(pathname)) {
      needAuth = false
      return 'empty'
    }

    if (/^\/expired(?=\/|$)/i.test(pathname)) {
      needAuth = true
      return 'empty'
    }

    if (/^\/ip-blocked(?=\/|$)/i.test(pathname)) {
      needAuth = false
      return 'empty'
    }

    if (/^\/viewer(?=\/|$)/i.test(pathname)) {
      needAuth = false
      return 'empty'
    }

    if (/^\/privacy(?=\/|$)/i.test(pathname)) {
      needAuth = false
      return 'empty'
    }

    return 'main'
  }

  const layout = getLayout()
  const Container = Layouts[layout]

  // const ipBlockedData = getTempEnvParam('ipBlocked')
  // if (
  //   ipBlockedData &&
  //   ipBlockedData.blocked &&
  //   !pathname.startsWith('/ip-blocked') &&
  //   !pathname.startsWith('/viewer')
  // ) {
  //   return <Navigate to="/ip-blocked" />
  // }

  const token = store.get('app.user.token')
  const inviteToken = store.get('app.user.invite_token')
  const isUserAuthorized = authorized || token != null || inviteToken != null
  const isUserLoading = loading
  const isPublic = layout === 'public'
  const isAuthLayout = layout === 'auth'
  // 로그인 후 원래 화면으로 복귀시킬 경로 화이트리스트 — 새 최상위 섹션 추가 시 여기와
  // 아래 UMK-부재 리다이렉트(shouldPreservePath) 두 곳에 함께 등록할 것
  const isPathPreservedRoute = /^\/(records|admin)(?=\/|$)/i.test(pathname)

  const BootstrappedLayout = () => {
    if (pathname.startsWith('/ip-blocked')) {
      if (getTempEnvParam('ipBlocked')?.blocked !== true) {
        return <Navigate to={DEFAULT_AUTH_PAGE} />
      }
      return <Container>{children}</Container>
    }

    // viewer, invite, privacy 엔드포인트는 모든 인증/UMK 체크 건너뛰기
    if (
      pathname.startsWith('/viewer') ||
      pathname.startsWith('/invite') ||
      pathname.startsWith('/privacy')
    ) {
      return <Container>{children}</Container>
    }

    if (isPublic !== true) {
      if (isUserLoading && needAuth) {
        return null
      }
      if (needAuth && !isUserAuthorized) {
        console.log(' auth - ', pathname, needAuth, isAuthLayout, isUserAuthorized)

        const redirectPath = isPathPreservedRoute
          ? `${DEFAULT_AUTH_PAGE}?path=${encodeURIComponent(pathname + location.search)}`
          : DEFAULT_AUTH_PAGE

        return <Navigate to={redirectPath} />
      }

      let appTypeInfo = {}
      try {
        appTypeInfo = JSON.parse(getEnvParam('appType') || '{}')
      } catch {
        appTypeInfo = {}
      }

      if (appTypeInfo.role !== 'admin' && appTypeInfo.role !== 'owner') {
        if (pathname.startsWith('/admin')) {
          deleteUMKFromLocal()
          history.push('/masterPW/login')
        }
      }

      const routerFunc = getRoutes()
      const routePaths = (routerFunc || []).map((r) => r.path)
      const matchesAnyRoute = routePaths.some((routePath) => {
        if (routePath.includes(':')) {
          const regex = new RegExp(`^${routePath.replace(/:[^/]+/g, '[^/]+')}$`)
          return regex.test(pathname)
        }

        const cleanPathname = pathname.split('?')[0]
        return cleanPathname === routePath
      })

      getUMKFromLocal()
        .then((value) => {
          if (value == null && isUserAuthorized) {
            const inviteType = getEnvParam('inviteType')
            const flowType = inviteType ? JSON.parse(inviteType).flow_type : null
            const userToken = store.get('app.user.token')
            const isRecordDetailRoute = /^\/records\/[^/]+$/.test(pathname)
            const isAdminRoute = /^\/admin(?=\/|$)/i.test(pathname)
            const shouldPreservePath = isRecordDetailRoute || isAdminRoute
            const redirectPath = shouldPreservePath
              ? `/masterPW/login?path=${encodeURIComponent(pathname)}`
              : '/masterPW/login'

            if (
              !matchesAnyRoute &&
              !pathname.startsWith('/ip-blocked') &&
              !pathname.startsWith('/viewer')
            ) {
              if (authType === 'login' || flowType === 'register' || flowType === 'join') {
                if (flowType === 'register' && userToken != null) {
                  history.push('/masterPW/register')
                }
                history.push(redirectPath)
              } else if (authType === 'register') {
                history.push('/masterPW/register')
              } else if (authType === 'change-password') {
                history.push('/masterPW/changePW')
              } else if (authType === 'signup' || flowType === 'signup') {
                if (flowType === 'signup' && userToken != null) {
                  history.push('/masterPW/register')
                }
                history.push('/auth/signup/register')
              } else {
                history.push(redirectPath)
              }
              // deleteUMKFromLocal()
            }
          }

          if (value && isUserAuthorized) {
            if (pathname.startsWith(DEFAULT_AUTH_PAGE)) {
              history.push('/')
            }
          }
        })
        .catch((error) => {
          console.error('Error fetching UMK:', error)
        })
    }

    return <Container>{children}</Container>
  }

  return (
    <Fragment>
      <Helmet titleTemplate={`${SERVICE_TITLE} | %s`} title={SERVICE_TITLE} />
      {BootstrappedLayout()}
      <PasskeyAutoFlow />
    </Fragment>
  )
}

export default connect(mapStateToProps)(Layout)
